import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import * as yup from 'yup';
import { useFormik } from 'formik';
import "./SignIn.scss";
import CircularProgress from '@mui/material/CircularProgress';
import { useLogin, login, resendVerificationCode } from './2faEndpoints';
import { UrlHelpers } from '../../lib/Helpers';
import { getUrl } from '../../domain';

export default function SignInWith2fa() {
    const _2faUser = useLogin();
    const [twoFactorProvider, setTwoFactorProvider] = React.useState(_2faUser.result?.twoFactorProvider || "Email");
    // Get ReturnUrl from query string
    let returnUrl = getUrl(UrlHelpers.getSafeUrl(new URLSearchParams(window.location.search).get('ReturnUrl') || "", "/account/logout"));

    // eslint-disable-next-line no-unused-vars
    const [showFormError, setShowFormError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null); // default to the other error

    const [twoFactorStatus, setTwoFactorStatus] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const twoFactorSending = React.useRef(false);
    const initialtwoFactorSent = React.useRef(false);

    const handleChangeTwoFactorProvider = (event) => {
        setTwoFactorProvider(event.target.value);
    };

    const initialValues = { code: '', rememberMachine: false, twoFactorProvider: twoFactorProvider };
    const validationSchema = yup.object({
        code: yup
            .string('Enter your authentication code')
            .required('Authentication code is required'),
    });

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                formik.values.twoFactorProvider = twoFactorProvider;
                setShowFormError(false);
                setErrorMessage(null);

                let data = await login(values);
                if (data) {
                    window.location = returnUrl;
                }
            }
            catch(err) {
                formik.setSubmitting(false);
                setShowFormError(true);
                if (err.response?.data?.errorMessage) {
                    setErrorMessage(err.response.data.errorMessage);
                }
                else
                {
                    setErrorMessage("The code is incorrect");
                }
            }
            finally {
                formik.setSubmitting(false);
            }
        },
    });

    const handleResend = async () => {

        if (twoFactorSending.current) {
            // already sending don't do anything
            return;
        }
        try {
            twoFactorSending.current = true;
            setLoading(true);
            let twoFactorStatusResult = await resendVerificationCode(twoFactorProvider);
            setTwoFactorStatus(twoFactorStatusResult.result);
        }
        catch(err) {
            setShowFormError(true);
            setTwoFactorStatus(false);
            if (err.response?.data?.errorMessage) {
                setErrorMessage(err.response.data.errorMessage);
            }
            else {
                setErrorMessage("An error occurred while attempting to send a verificaiton code via: " + twoFactorProvider);
            }
        }
        finally {
            setLoading(false);
            twoFactorSending.current = false;
        }
    }

    if (!initialtwoFactorSent.current)
    {
        handleResend();
        initialtwoFactorSent.current = true;
    }

    const resendButton = () => {
        return (
            <div>
                <Button sx={{pl: 0, textTransform: 'none'}}
                    type="button"
                    disabled={formik.isSubmitting}
                    onClick={handleResend}
                >
                    Re-send code
                </Button>
            </div>
        )
    }

    return (
        <>
            <Box
                sx={{
                    marginTop: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <form onSubmit={formik.handleSubmit}>
                    {showFormError && (
                        <Alert sx={{ mt: 2 }} severity="error">{errorMessage}</Alert>
                    )}
                    <FormControl fullWidth sx={{mt: 2}}>
                        <InputLabel id="2fa-provider-select-label">Multi-factor Provider</InputLabel>
                        <Select
                            labelId="2fa-provider-select-label"
                            id="2fa-provider-select"
                            value={twoFactorProvider}
                            label="Multi-factor Provider"
                            onChange={handleChangeTwoFactorProvider}
                        >
                            {_2faUser.result?.validTwoFactorProviders.map(provider => (
                                <MenuItem value={provider}>{provider}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField sx={{ mt: 2 }}
                        type="text"
                        name="code"
                        label={ (twoFactorProvider === 'Authenticator' ? "Authentication" : "Verification") + " Code"}
                        data-cy-id="signIn2fa-form-code"
                        fullWidth
                        value={formik.values.code}
                        onChange={formik.handleChange}
                        error={formik.touched.code && Boolean(formik.errors.code)}
                        helperText={formik.touched.code && formik.errors.code}
                    />
                    {twoFactorProvider !== 'Authenticator' && (loading ? (
                        <Alert severity="info" sx={{mt: 2}}>
                            Sending code..
                            <div>
                            <CircularProgress size="1rem" sx={{mt: 2}} aria-label={"Loading..."} title={"Loading..."} />
                            </div>
                        </Alert>
                    ):(
                        <>
                        {twoFactorStatus ? (
                            <Alert severity="success" sx={{mt: 2}}>
                                Successfully sent code
                                {resendButton()}
                            </Alert>
                        ):(
                            <Alert severity="error" sx={{mt: 2}}>
                                Failed to send code
                                {resendButton()}
                            </Alert>
                        )
                        }
                        </>
                    ))}
                    <FormControlLabel
                        name="rememberMachine"
                        value={formik.values.rememberMachine}
                        onChange={formik.handleChange}
                        control={<Checkbox value="rememberMachine" color="primary" />}
                        label="Remember Machine"
                        data-cy-id="signIn2fa-form-remember"
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        data-cy-id="signIn2fa-form-submit"
                        disabled={formik.isSubmitting}
                        sx={{ mt: 2, mb: 2 }}
                    >
                        Sign In
                    </Button>
                </form>
                { twoFactorProvider === 'Authenticator' && (
                    <Link href={getUrl("/account/loginWith2faRecoveryCode?ReturnUrl=" + encodeURIComponent(returnUrl))} variant="body2">
                        {"Don't have access to your authenticator device? You can sign in with a recovery code."}
                    </Link>
                )}
            </Box>
        </>
    );
}